import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Search, Loader2, Package, X } from "lucide-react";
import { formatCurrency } from "@/lib/api";

function escapeRegex(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function Highlight({ text, query }) {
  if (!text) return null;
  const q = (query || "").trim();
  if (!q) return <>{text}</>;
  const re = new RegExp(`(${escapeRegex(q)})`, "ig");
  const parts = String(text).split(re);
  return (
    <>
      {parts.map((p, i) =>
        p.toLowerCase() === q.toLowerCase()
          ? <mark key={i} className="bg-[#39FF14]/25 text-[#F8F9FA] rounded-sm px-0.5">{p}</mark>
          : <span key={i}>{p}</span>
      )}
    </>
  );
}

/**
 * Iteration 4 reusable product typeahead. Mirrors CustomerSearch and hits
 * `/api/products/search?q=&limit=20` (server-side substring on search_blob).
 *
 * Props:
 *   value        — currently-selected product id
 *   onChange(p)  — called with the full product object (or null when cleared)
 *   product      — optional pre-loaded product for the current value
 *   placeholder  — optional input placeholder
 *   testid       — optional data-testid stub
 *   disabled     — disable the input
 *   autoFocus    — focus the input on mount
 */
export default function ProductSearch({
  value, onChange, product, placeholder = "Search by brand, model, category…",
  testid = "product-search", disabled, autoFocus,
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [selected, setSelected] = useState(product || null);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (product && product.id !== selected?.id) setSelected(product);
    // eslint-disable-next-line
  }, [product?.id]);

  useEffect(() => { if (!value) setSelected(null); }, [value]);

  const fetchResults = useCallback(async (q) => {
    if (!q || q.trim().length < 1) { setResults([]); setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/products/search?q=${encodeURIComponent(q.trim())}&limit=20`);
      setResults(Array.isArray(data) ? data : []);
    } catch { setResults([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchResults(query), 180);
    return () => clearTimeout(debounceRef.current);
  }, [query, fetchResults]);

  useEffect(() => { setHighlight(-1); }, [results]);

  useEffect(() => {
    const onDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const choose = (p) => {
    setSelected(p);
    setOpen(false);
    setQuery("");
    setResults([]);
    onChange?.(p);
  };

  const clear = () => {
    setSelected(null);
    setQuery("");
    setResults([]);
    setOpen(true);
    onChange?.(null);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const onKeyDown = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(h + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => Math.max(h - 1, -1)); }
    else if (e.key === "Enter") {
      if (highlight >= 0 && highlight < results.length) {
        e.preventDefault();
        choose(results[highlight]);
      }
    } else if (e.key === "Escape") { e.preventDefault(); setOpen(false); }
  };

  const showEmpty = useMemo(
    () => open && query.trim().length >= 1 && !loading && results.length === 0,
    [open, query, loading, results]
  );

  if (selected) {
    const label = [selected.brand, selected.model].filter(Boolean).join(" ") || selected.name || "(unnamed)";
    return (
      <div className="border border-[#3A4640] rounded-md bg-[#121815] p-3 flex items-center gap-3" data-testid={`${testid}-selected`}>
        <div className="w-10 h-10 rounded flex items-center justify-center bg-[#0B0F0C] border border-[#3A4640] shrink-0 overflow-hidden">
          {selected.image_url
            ? <img src={selected.image_url} alt="" className="w-full h-full object-cover" />
            : <Package size={16} className="text-[#39FF14]" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-bold text-[#F8F9FA] truncate">{label}</div>
          <div className="text-[11px] text-[#8A8F98] mt-0.5 flex flex-wrap gap-x-3">
            {selected.category && <span>{selected.category}</span>}
            {(selected.unit_price ?? selected.price) != null && (
              <span className="font-mono text-[#39FF14]">{formatCurrency(selected.unit_price ?? selected.price)}</span>
            )}
            {selected.stock != null && <span>Stock: {selected.stock}</span>}
          </div>
        </div>
        {!disabled && (
          <button type="button" onClick={clear} className="text-[#8A8F98] hover:text-red-500 p-1" title="Change product" data-testid={`${testid}-clear`}>
            <X size={14} />
          </button>
        )}
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative" data-testid={testid}>
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8F98] pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          disabled={disabled}
          autoFocus={autoFocus}
          autoComplete="off"
          placeholder={placeholder}
          data-testid={`${testid}-input`}
          className="w-full h-10 pl-9 pr-9 bg-[#121815] border border-[#3A4640] rounded-md text-sm text-[#F8F9FA] placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-[#166534] focus:border-[#39FF14]/40 transition-shadow"
        />
        {loading && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A8F98] animate-spin" />}
      </div>

      {open && (loading || results.length > 0 || showEmpty) && (
        <div
          className="absolute z-50 mt-1.5 left-0 right-0 bg-[#121815] border border-[#3A4640] rounded-md shadow-xl max-h-[380px] overflow-y-auto"
          data-testid={`${testid}-dropdown`}
        >
          {loading && results.length === 0 && (
            <div className="px-4 py-6 text-sm text-[#8A8F98] flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Searching…</div>
          )}
          {results.map((p, idx) => {
            const isHighlighted = idx === highlight;
            const label = [p.brand, p.model].filter(Boolean).join(" ") || p.name || "(unnamed)";
            return (
              <button
                type="button"
                key={p.id}
                onMouseEnter={() => setHighlight(idx)}
                onClick={() => choose(p)}
                data-testid={`${testid}-result-${p.id}`}
                className={`w-full text-left px-4 py-3 border-b border-[#3A4640] last:border-0 flex gap-3 items-start transition-colors ${isHighlighted ? "bg-[#0B0F0C]" : "hover:bg-[#0B0F0C]"}`}
              >
                <div className={`w-9 h-9 rounded flex items-center justify-center shrink-0 overflow-hidden ${isHighlighted ? "bg-[#39FF14]/15" : "bg-[#0B0F0C] border border-[#3A4640]"}`}>
                  {p.image_url
                    ? <img src={p.image_url} alt="" className="w-full h-full object-cover" />
                    : <Package size={14} className={isHighlighted ? "text-[#39FF14]" : "text-[#8A8F98]"} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-bold text-[#F8F9FA] truncate">
                    <Highlight text={label} query={query} />
                  </div>
                  <div className="text-[11px] text-[#8A8F98] mt-0.5 flex flex-wrap gap-x-3">
                    {p.category && <span><Highlight text={p.category} query={query} /></span>}
                    {(p.unit_price ?? p.price) != null && (
                      <span className="font-mono text-[#39FF14]">{formatCurrency(p.unit_price ?? p.price)}</span>
                    )}
                    {p.stock != null && <span>Stock: {p.stock}</span>}
                  </div>
                </div>
              </button>
            );
          })}

          {showEmpty && (
            <div className="px-4 py-5 text-sm text-[#8A8F98]" data-testid={`${testid}-empty`}>
              No product found for <b>&ldquo;{query}&rdquo;</b>.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
